import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
// import { API_CONFIG } from '../config/api';
import { API_CONFIG } from '../config/api';
import { Heart, Apple, Brain, Dumbbell, Briefcase, BookOpen, Globe, Users, DollarSign, Leaf } from 'lucide-react';

type LearnCategoryColor = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
type LearnCategory = {
  label: string;
  to: string;
  color: LearnCategoryColor;
  description: string;
  Icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
};

// Inherent Attributes - Core personal wellness dimensions
const inherentCategories: LearnCategory[] = [
  { label: 'Spiritual', to: '/panel/learn/spiritual-assessment', color: 1, description: 'Find peace and purpose through meditations, mindfulness, and spiritual guidance.', Icon: Heart },
  { label: 'Nutritional', to: '/panel/learn/nutritional-assessment', color: 2, description: 'Discover healthy recipes, meal plans, and expert advice for a balanced diet.', Icon: Apple },
  { label: 'Emotio-Psycho-Rational', to: '/panel/learn/emotio-psycho-rational-assessment', color: 3, description: 'Enhance your emotional intelligence and mental clarity with expert content.', Icon: Brain },
  { label: 'Physical', to: '/panel/learn/physical-wellness-assessment', color: 10, description: 'Engage in workouts, fitness challenges, and wellness tips to energize your body.', Icon: Dumbbell },
  { label: 'Legendarial', to: '/panel/learn/legendarial-assessment', color: 4, description: 'Explore timeless stories and myths that inspire and teach profound lessons.', Icon: BookOpen },
];

// Coherent Attributes - Social and contextual dimensions
const coherentCategories: LearnCategory[] = [
  { label: 'Occupational', to: '/panel/learn/occupational-assessment', color: 9, description: 'Advance your career with skills, strategies, and insights for professional growth.', Icon: Briefcase },
  { label: 'Religio-Traditio-Cultural', to: '/panel/learn/religio-traditio-cultural-assessment', color: 5, description: 'Deepen your understanding of diverse religions, traditions, and cultures.', Icon: Globe },
  { label: 'Socio-Relational', to: '/panel/learn/socio-relational-assessment', color: 7, description: 'Learn about social dynamics, improve communication, and build better relationships.', Icon: Users },
  { label: 'Financial', to: '/panel/learn/financial-assessment', color: 8, description: 'Master your finances with tips on budgeting, investing, and wealth management.', Icon: DollarSign },
  { label: 'Environmental', to: '/panel/learn/environmental-assessment', color: 6, description: 'Embrace sustainable living and learn how to protect our planet.', Icon: Leaf },
];

// Assessment Tools - Comprehensive wellness evaluation tools
const assessmentTools: LearnCategory[] = [
  { label: 'Physical Health', to: '/panel/learn/physical-wellness-assessment', color: 10, description: 'Comprehensive physical health evaluation including fitness, wellness, and health metrics.', Icon: Dumbbell },
  { label: 'Emotional Wellbeing', to: '/panel/learn/emotio-psycho-rational-assessment', color: 3, description: 'Assess emotional intelligence, psychological resilience, and mental clarity indicators.', Icon: Brain },
  { label: 'Spiritual Assessment', to: '/panel/learn/spiritual-assessment', color: 1, description: 'Evaluate spiritual wellbeing and life purpose through structured assessment tools.', Icon: Heart },
  { label: 'Nutritional Analysis', to: '/panel/learn/nutritional-assessment', color: 2, description: 'Analyze dietary habits, nutrition intake, and eating patterns for optimal health.', Icon: Apple },
  { label: 'Financial Wellness', to: '/panel/learn/financial-assessment', color: 8, description: 'Evaluate financial health, budgeting skills, and wealth management strategies.', Icon: DollarSign },
  { label: 'Environmental Impact', to: '/panel/learn/environmental-assessment', color: 6, description: 'Assess environmental awareness and sustainable living practices.', Icon: Leaf },
  { label: 'Occupational Health', to: '/panel/learn/occupational-assessment', color: 9, description: 'Evaluate career satisfaction, work-life balance, and professional development.', Icon: Briefcase },
  { label: 'Social Relations', to: '/panel/learn/socio-relational-assessment', color: 7, description: 'Analyze social connections, relationship quality, and communication skills.', Icon: Users },
  { label: 'Cultural Heritage', to: '/panel/learn/religio-traditio-cultural-assessment', color: 5, description: 'Explore cultural identity, traditional values, and heritage connections.', Icon: Globe },
  { label: 'Life Stories', to: '/panel/learn/legendarial-assessment', color: 4, description: 'Discover personal narratives and life lessons through legendary storytelling.', Icon: BookOpen },
];

const LearnPage: React.FC = () => {
  const navigate = useNavigate();

  const { user } = useAuth();
  const [hasCompletedAssessments, setHasCompletedAssessments] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [filterLoaded, setFilterLoaded] = useState(false);
  const [assessmentLoaded, setAssessmentLoaded] = useState(false);
  const [assessmentFilter, setAssessmentFilter] = useState<string>('none');
  const [resourceFilter, setResourceFilter] = useState<string>('all');

  // Fetch user subscription filter from backend (plan metadata)
  useEffect(() => {
    const fetchAssessmentFilter = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_CONFIG.ENDPOINTS.BILLING_SUBSCRIPTION}/me`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.ok) {
          const result = await response.json();
          console.log('Subscription data:', result.data);
          const accessArr = result.data?.subscription?.plan?.metadata?.access?.userNav || [];
          const filter = result.data?.subscription?.plan?.metadata?.assessmentFilter || 'none';
          const resourceFilterValue = result.data?.subscription?.plan?.metadata?.resourceFilter || 'all';
          console.log('Fetched resourceFilter:', resourceFilterValue);
          console.log('Subscription plan ID:', result.data?.subscription?.plan?._id);
          console.log('Subscription plan name:', result.data?.subscription?.plan?.name);
          // Only apply filter if 'learn' is in access
          if (accessArr.includes('learn')) {
            setAssessmentFilter(filter);
            setResourceFilter(resourceFilterValue);
            console.log('Set resourceFilter to:', resourceFilterValue);
          } else {
            setAssessmentFilter('none');
            setResourceFilter('all');
          }
        } else {
          setAssessmentFilter('none');
        }
      } catch {
        setAssessmentFilter('none');
      } finally {
        setFilterLoaded(true);
      }
    };
    fetchAssessmentFilter();
  }, []);

  useEffect(() => {
    checkCompletedAssessments();
  }, []);

  // Synchronize loading state: only set isLoading to false when BOTH fetches complete
  useEffect(() => {
    if (filterLoaded && assessmentLoaded) {
      setIsLoading(false);
    }
  }, [filterLoaded, assessmentLoaded]);

  const checkCompletedAssessments = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Check for spiritual assessment
      const response = await fetch(`${API_CONFIG.ENDPOINTS.SPIRITUAL}/latest`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setHasCompletedAssessments(true);
        }
      }
    } catch (error) {

    } finally {
      setAssessmentLoaded(true);
    }
  };

  // Helper function to determine if a category is allowed based on assessment filter
  const getAllowedCategory = (filter: string, categoryLabel: string): boolean => {
    // Physical category is always available for all subscribers
    if (categoryLabel === 'Physical') return true;
    
    if (filter === 'none') return true;
    
    switch (filter) {
      case 'dass21':
        return categoryLabel === 'Emotio-Psycho-Rational';
      case 'spiritual':
        return categoryLabel === 'Spiritual';
      case 'nutritional':
        return categoryLabel === 'Nutritional';
      case 'financial':
        return categoryLabel === 'Financial';
      default:
        return true;
    }
  };

  // Helper function to determine if resources are allowed for a category
  const areResourcesAllowed = (categoryLabel: string): boolean => {
    // Physical resources are always available for all subscribers
    if (categoryLabel === 'Physical') return true;
    
    const allowed = resourceFilter === 'all' ? true : resourceFilter === 'none' ? false : true;
    console.log(`areResourcesAllowed(${categoryLabel}): resourceFilter=${resourceFilter}, allowed=${allowed}`);
    return allowed;
  };

  const renderCategorySection = (title: string, categories: LearnCategory[], isMainTitle: boolean = false, showCards: boolean = true) => {
    // Show all categories but mark non-allowed ones as disabled when filter is active
    const displayCategories = categories;
    
    return (
      <div className="learn__categories-section">
        {isMainTitle ? (
          <h2 className="learn__main-title">{title}</h2>
        ) : (
          <h3 className="learn__categories-title">{title}</h3>
        )}
        {showCards && displayCategories.length > 0 && (
          <div className="learn__categories-grid">
            {displayCategories.map((category) => {
              // Check if category assessment is allowed based on assessment filter
              const isAssessmentAllowed = assessmentFilter === 'none' || getAllowedCategory(assessmentFilter, category.label);
              // Check if resources are allowed
              const isResourceAllowed = areResourcesAllowed(category.label);
              
              return (
                <div
                  key={category.label}
                  className={`learn__category-card learn__category-card--c${category.color} ${assessmentFilter !== 'none' && isAssessmentAllowed ? 'learn__category-card--highlighted' : ''} ${assessmentFilter !== 'none' && !isAssessmentAllowed ? 'learn__category-card--disabled' : ''}`}
                >
                  <img
                    src={
                      category.label === 'Spiritual' ? require('../assets/Understanding Spiritual Wellness.png') :
                      category.label === 'Nutritional' ? require('../assets/Nutritional Resources.png') :
                      category.label === 'Emotio-Psycho-Rational' ? require('../assets/Emotio.png') :
                      category.label === 'Physical' ? require('../assets/physical.png') :
                      category.label === 'Occupational' ? require('../assets/physical.png') :
                      category.label === 'Legendarial' ? require('../assets/Legendarial.png') :
                      category.label === 'Religio-Traditio-Cultural' ? require('../assets/religio.png') :
                      category.label === 'Socio-Relational' ? require('../assets/socio.png') :
                      category.label === 'Financial' ? require('../assets/Financial.png') :
                      category.label === 'Environmental' ? require('../assets/environmental.png') :
                      require('../assets/socio.png')
                    }
                    alt={category.label + ' banner'}
                    className="learn__category-image"
                  />
                  <div className="learn__category-content">
                    <h3 className="learn__category-title">{category.label}</h3>
                    <p className="learn__category-description">{category.description}</p>
                    <div className="learn__category-buttons">
                      <button 
                        className={`learn__category-button ${(isLoading || !isAssessmentAllowed) ? 'learn__category-button--locked' : ''}`}
                        onClick={(e) => { e.stopPropagation(); navigate(category.to); }}
                        disabled={isLoading || !isAssessmentAllowed}
                        title={!isAssessmentAllowed ? 'Subscribe to unlock feature' : ''}
                      >
                        {!isAssessmentAllowed && <span className="learn__category-button__lock">🔒</span>}
                        Assessment
                      </button>
                      <button 
                        className={`learn__category-button ${(isLoading || !isResourceAllowed) ? 'learn__category-button--locked' : ''}`}
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          console.log(`Resource button clicked for ${category.label}, isResourceAllowed: ${isResourceAllowed}`);
                          // Special handling for Physical category
                          if (category.label === 'Physical') {
                            navigate('/panel/learn/physical-resources');
                          } else {
                            navigate(category.to.replace('-assessment', '-resources'));
                          }
                        }}
                        disabled={isLoading || !isResourceAllowed}
                        title={!isResourceAllowed ? 'Subscribe to unlock feature' : ''}
                      >
                        {!isResourceAllowed && <span className="learn__category-button__lock">🔒</span>}
                        Resources
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="learn">
      {isLoading ? (
        <div className="learn__loading">
          <div className="learn__loading-spinner">Loading...</div>
        </div>
      ) : (
        <>
          {/* Assessment Tools Section - Main Title Only (No Cards) */}
          {renderCategorySection('Assessment Tools', assessmentTools, true, false)}

          {/* Inherent Attributes Section - Subsection Title with Cards */}
          {renderCategorySection('Inherent Attributes', inherentCategories, false, true)}

          {/* Coherent Attributes Section - Subsection Title with Cards */}
          {renderCategorySection('Coherent Attributes', coherentCategories, false, true)}
        </>
      )}
    </div>
  );
};

export default LearnPage;





